/* eslint-disable @typescript-eslint/no-unused-vars */
import { PrinterOutlined, UploadOutlined } from '@ant-design/icons'
import { useNotification } from '@refinedev/core'
import { Button, Col, Form, Input, Modal, Typography, Upload } from 'antd'
import moment from 'moment'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useModalForm as useModalFormAntd } from 'sunflower-antd/lib/useModalForm'
import { httpClient, ROOT_URL } from '../../App'
import { purposeOfRemittanceItemNames } from '../../components/inputs/select/purposeOfRemittance.constants'
import { SelectField } from '../../components/inputs/select/select'
import {
  getInvoiceStatusOptionLabel,
  paymentRequestInvoicePayablePossibleMoves,
} from '../../components/inputs/select/statusSelect.constants'
import TextContent from '../../components/modal/admin/text-content'
import { TOKEN_KEY } from '../../components/providers/authProvider'
import { ICompany } from '../../interfaces/companies'
import FeesPayer from '../../types/feesPayer.type'
import PaymentStatus from '../../types/paymentStatus.type'
import { acceptUploadFileTypes } from '../../utils/acceptUploadFileTypes'
import { beforeUpload } from '../../utils/beforeUpload'
import {
  clearFieldErrorMessageOnChange,
  getFileListFromEvent,
} from '../../utils/form.constants'
import { useNotificationOkCancel } from '../../utils/hooks/useNotificationOkCancel.hook'
import { renderWorldcheckVerificationStatus } from '../../utils/renderWorldCheckVerificationStatusV2'
import { ButtonListFooter } from '../components/buttonsList'
import { confirmModalUnsavedChangesProps } from '../components/constants'
import {
  IDocumentsChanges,
  IInvoicePayableFormValues,
} from '../components/interface'
import './EditInvoicePayable.css'
import { useInvoicePayable } from './useInvoicePayable'
import { useUpdateInvoicePayable } from './useUpdateInvoicePayable'
import { getDecimalsBySymbol } from "../../utils/list.utils";

interface Props {
  editingId?: number
  onClose: () => void
  onComplete: () => void
  wiseButtonsAvailable: boolean
}
export const Fiat = [
  'USD',
  'EUR',
  'HKD',
  'AUD',
  'JPY',
  'CHF',
  'CNY',
  'AED',
  'SGD',
  'GBP',
]
export const Crypto = ['USDT', 'DAI', 'ETH']

export const EditInvoicePayable: FC<Props> = ({
  editingId,
  onClose,
  onComplete,
  wiseButtonsAvailable,
}) => {
  const { data, isLoading, fetch } = useInvoicePayable(editingId)
  const [formChanged, setFormChanged] = useState(false)
  const { open: openNotification } = useNotification()

  const wiseConfirm = useCallback(async () => {
    if (wiseButtonsAvailable) {
      try {
        await httpClient.post(`${ROOT_URL}/api/v1/wise/confirmTransaction`, {
          id: editingId,
        })
        if (openNotification) {
          openNotification?.({
            key: 'success_transaction_confirm',
            type: 'success',
            message: `Request sent successfully`,
          })
        }
        onClose()
      } catch (error) {
        openNotification?.({
          key: 'error_confirm',
          type: 'error',
          message: 'Request failed',
          description: 'Request failed while confirming the transaction.',
        })
      }
    }
  }, [wiseButtonsAvailable, onClose, editingId, openNotification])

  const wiseCancel = useCallback(async () => {
    if (wiseButtonsAvailable) {
      try {
        await httpClient.post(`${ROOT_URL}/api/v1/wise/cancelTransaction`, {
          id: editingId,
        })
        openNotification?.({
          key: 'success_transaction_cancel',
          type: 'success',
          message: `Request sent successfully`,
        })
        onClose()
      } catch (error) {
        openNotification?.({
          key: 'error_cancel',
          type: 'error',
          message: 'Request failed',
          description: 'Request failed while canceling the transaction.',
        })
      }
    }
  }, [wiseButtonsAvailable, onClose, editingId, openNotification])

  const defaultFormValues = useMemo(() => {
    if (data) {
      const {
        paymentRequest: {
          dateOfTransaction = undefined,
          amount = undefined,
          totalSendAmount = undefined,
          currency = {
            code: undefined,
            name: undefined,
          },
          serviceProvider = undefined,
          company = undefined,
          bank = undefined,
          bankAccount = undefined,
          specialInstructions = undefined,
          purposeOfRemittance = undefined,
          paymentCurrency = {
            symbol: undefined,
          },
          futurePaymentDate,
        } = {},
        documentNames,
        status,
        reason,
      } = data

      return {
        // dateOfTransaction,
        // amount: totalSendAmount,
        // currencyCode: currency?.code || paymentCurrency?.symbol,
        // serviceProvider: {
        //   data: serviceProvider,
        //   value:
        //     serviceProvider?.deleted && status === PaymentStatus.PENDING
        //       ? ""
        //       : serviceProvider?.name,
        // },
        // companyId: company?.name,
        // bankId: bank?.name,
        // bankAccountId:
        //   bankAccount &&
        //   `${bankAccount.accountNumber} [${bankAccount.currencyCode}]`,
        // specialInstructions,
        documents: documentNames?.map((fileName: string) => ({
          name: fileName,
        })),
        status,
        // purposeOfRemittance,
        // walletAddress: data?.paymentRequest?.beneficiaryWallet?.address,
        // walletNetwork: data?.paymentRequest?.beneficiaryWallet?.network?.name,
        reason,
        // userCreate: data?.paymentRequest?.createdByUser?.fullName,
        // smartUID: data?.paymentRequest?.createdByUser?.smartUID,
        futurePaymentDate,
      }
    } else {
      return undefined
    }
  }, [data])

  const { close, modalProps, formProps, show } = useModalFormAntd({})

  const serviceProvider = Form.useWatch('serviceProvider', {
    form: formProps.form,
  })

  useEffect(() => {
    formProps?.form?.setFieldsValue(defaultFormValues)
    // if (
    //   defaultFormValues?.serviceProvider?.data?.deleted &&
    //   defaultFormValues?.status === PaymentStatus.PENDING
    // ) {
    //   formProps?.form?.validateFields(["serviceProvider"]);
    // }
  }, [defaultFormValues, formProps?.form])

  useEffect(() => {
    if (editingId !== undefined) {
      show()
    } else {
      close()
    }
  }, [close, editingId, show])

  const { update, isLoading: isUpdating } = useUpdateInvoicePayable(editingId)

  const [companyData, setCompanyData] = useState<ICompany>()
  const [initialDocuments, setInitialDocuments] = useState<string[]>([])
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(
    PaymentStatus.DRAFT
  )
  const formIsLoading = isLoading || isUpdating
  const [activeFeesPayer, setActiveFeesPayer] = useState<FeesPayer>(
    FeesPayer.SENDER
  )

  const status = Form.useWatch('status', { form: formProps.form })

  useEffect(() => {
    if (data) {
      const {
        paymentRequest: {
          customer = undefined,
          createdByUser = undefined,
          currency = undefined,
          company = undefined,
          bank = undefined,
          bankAccount = undefined,
          paymentCurrency = {
            symbol: undefined,
          },
          feesPayer = undefined,
          beneficiaryCompanyAddress = undefined,
          beneficiaryFullName = undefined,
          beneficiarySmartUID = undefined,
        } = {},
        status,
        documentNames,
      } = data

      setInitialDocuments(documentNames ?? [])

      if (
        company ||
        beneficiaryCompanyAddress ||
        beneficiaryFullName ||
        beneficiarySmartUID
      ) {
        setCompanyData({
          address1: beneficiaryCompanyAddress ?? company?.address1 ?? '',
          name: beneficiaryFullName ?? company?.name ?? '',
          smartUID: beneficiarySmartUID ?? company?.smartUID ?? '',
          country: company?.country ?? '',
          postalCode: company?.postalCode ?? '',
          stateProvince: company?.stateProvince ?? '',
          city: company?.city ?? '',
          worldCheckVerification: company?.worldCheckVerification,
        })
      }

      if (status) {
        setPaymentStatus(status)
      }
      setActiveFeesPayer(feesPayer || FeesPayer.SENDER)
    }
  }, [data])

  const feesPayerRadioGroup = useMemo(() => {
    // if (accountData?.currencyCode === currencyData?.code) {
    return (
      <>
        <hr />
        <p className={'invoice-payable-radio-group-title'}>
          Bank fees payable by{' '}
          {activeFeesPayer === FeesPayer?.SENDER
            ? 'Remitter (Our)'
            : 'Beneficiary (Shared)'}
        </p>
      </>
    )
    // }
  }, [activeFeesPayer])

  const onSubmit = useCallback(
    async (formValues: IInvoicePayableFormValues) => {
      if (!editingId) {
        return
      }

      const documentChanges: IDocumentsChanges = {
        new: formValues.documents
          ?.filter((document) => document.response)
          .map(({ response }) => response ?? ''),
        deleted: initialDocuments.filter(
          (initialFileName) =>
            !formValues.documents?.find(({ name }) => {
              return name === initialFileName
            })
        ),
      }

      const isSuccess = await update({
        documentChanges,
        status: formValues.status,
        reason:
          formValues.status === PaymentStatus.APPROVED ||
            formValues.status === PaymentStatus.PENDING
            ? null
            : formValues?.reason,
      })
      if (isSuccess) {
        fetch()
        onComplete()
      }
    },
    [editingId, initialDocuments, update, fetch, onComplete]
  )

  const statusSelectItems = useMemo(
    () =>
      paymentRequestInvoicePayablePossibleMoves.get(
        data?.status || PaymentStatus.DRAFT
      ) || [PaymentStatus.DRAFT],
    [data?.status]
  )

  const onUnsavedChangesOk = useCallback(() => {
    formProps?.form?.resetFields()
    setCompanyData(undefined)
    setFormChanged(false)
    onClose()
  }, [onClose, formProps?.form])

  const {
    openNotification: openUnsavedChangesNotification,
    contextHolder: unsavedChangesContextHolder,
  } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: onClose,
  })

  const onModalCancel = useCallback(() => {
    if (formIsLoading) return
    onClose()
  }, [onClose, formIsLoading])

  const isDisabledForm =
    (paymentStatus !== PaymentStatus.PENDING &&
      paymentStatus !== PaymentStatus.POSTPONED) ||
    formIsLoading

  const isButtonsDisabled =
    paymentStatus === PaymentStatus.COMPLETED ||
    paymentStatus === PaymentStatus.APPROVED ||
    paymentStatus === PaymentStatus.PROCESSING ||
    paymentStatus === PaymentStatus.REJECTED ||
    paymentStatus === PaymentStatus.FAILED ||
    paymentStatus === PaymentStatus.CANCELLED ||
    formIsLoading

  const formatNumber = useCallback((value: number | string, decimals?: number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return typeof decimals === 'number' ? (0).toFixed(decimals) : '0';
    if (typeof decimals === 'number') {
      return num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    }
    return num.toLocaleString('en-US');
  }, []);

  return (
    <>
      <Modal
        {...modalProps}
        onCancel={onModalCancel}
        title='Edit invoice'
        className='smartModalTitle'
        afterClose={onUnsavedChangesOk}
        destroyOnClose
        maskClosable={false}
        footer={
          <Col>
            <ButtonListFooter
              handleSave={formProps?.form?.submit}
              handleSend={onModalCancel}
              isLoading={formIsLoading}
              hideSendBtn={true}
              isSaveBtnDisabled={!formChanged}
              isDisabled={isButtonsDisabled || !formChanged || formIsLoading}
            />
            {data?.printableFormUrl && (
              <Button
                icon={<PrinterOutlined />}
                type={'link'}
                href={data?.printableFormUrl}
                target={'_blank'}>
                &nbsp;Print
              </Button>
            )}
          </Col>
        }>
        {unsavedChangesContextHolder}
        <Form
          {...formProps}
          layout='vertical'
          onFinish={onSubmit}
          validateTrigger={false}
          onValuesChange={(e) => {
            setFormChanged(true)
            clearFieldErrorMessageOnChange(formProps?.form)(e)
          }}
          disabled={isDisabledForm}>
          <div
            style={{
              display: 'flex',
              width: '100%',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <Typography className='text-label' style={{ textAlign: 'start' }}>
              Send Money order ID:
            </Typography>
            <Typography className='text-label' style={{ textAlign: 'start' }}>
              #{data?.paymentRequest.id}
            </Typography>
          </div>
          <hr />
          <TextContent
            label='Send Date'
            value={moment(data?.paymentRequest?.dateOfTransaction).format(
              'DD/MM/YYYY'
            )}
          />
          <TextContent
            label='Total to pay'
            value={
              `${data?.paymentRequest?.totalSendAmount
                ? formatNumber(data?.paymentRequest?.totalSendAmount, getDecimalsBySymbol(data?.paymentRequest?.paymentCurrency?.symbol ?? ''))
                : 0
              } ${data?.paymentRequest?.paymentCurrency?.symbol}`
            }
            boldValue
          />
          {data?.remiter?.type === 'INTERNAL' ? null : (
            <div
              style={{
                display: 'flex',
                width: '100%',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'end',
              }}>
              <Typography
                className='text-label'
                style={{
                  textAlign: 'start',
                  fontSize: '12px',
                  color:
                    data?.paymentRequest?.isConfirmed ||
                      paymentStatus === PaymentStatus.COMPLETED
                      ? '#18B853'
                      : 'rgba(242, 138, 40, 1)',
                }}>
                {data?.paymentRequest?.isConfirmed ||
                  paymentStatus === PaymentStatus.COMPLETED
                  ? '*User has paid'
                  : '*User has not paid'}
              </Typography>
            </div>
          )}
          {feesPayerRadioGroup}
          <hr />
          <TextContent
            label='Customer'
            value={data?.paymentRequest?.customer?.fullName || ''}
            boldValue={true}
            boldLabel={true}
          />
          <TextContent
            label='Name'
            value={data?.paymentRequest?.customer?.email || 'N/A'}
          />
          <TextContent
            label='SMART UID'
            value={data?.paymentRequest?.customer?.smartUID || 'N/A'}
          />
          <hr />
          <>
            <TextContent label='Payment Method' value='Portfolio' />
            <TextContent
              label='Currency'
              value={data?.paymentRequest?.paymentCurrency?.symbol || 'N/A'}
            />
          </>
          <TextContent
            label='Reference Number'
            value={data?.paymentRequest?.referenceNumber || 'N/A'}
          />
          {data?.paymentRequest?.futurePaymentDate && (
            <>
              <TextContent
                label='Future date'
                value={
                  data?.paymentRequest?.futurePaymentDate
                    ? moment(data?.paymentRequest?.futurePaymentDate).format(
                      'DD/MM/YYYY'
                    )
                    : ''
                }
              />
            </>
          )}
          <hr />
          {data?.paymentRequest?.receiveUser ? (
            <>
              <TextContent label='Transfer method' value='SMART UID' />
              <TextContent
                label='Beneficiary'
                value={data?.paymentRequest?.receiveUser?.smartUID}
                boldValue={true}
                boldLabel={true}
              />
            </>
          ) : (
            <>
              <TextContent
                label='Beneficiary'
                value={companyData?.name || ''}
                boldValue={true}
                boldLabel={true}
              />
              {companyData?.worldCheckVerification &&
                renderWorldcheckVerificationStatus(
                  companyData.worldCheckVerification.status
                )}
              <TextContent
                label='Address'
                value={companyData?.address1 || ''}
              />
              <TextContent label='Country' value={companyData?.country || ''} />
              <br />
              {/* <Details
                show={showCompanyDetails}
                setShow={setShowCompanyDetails}
                name={"beneficiary"}
                fields={companyDetailsFields}
                data={companyData}
              /> */}
              {Fiat.includes(
                data?.paymentRequest?.receiveCurrency?.symbol || ''
              ) ? (
                <>
                  <TextContent
                    label='Bank Name'
                    value={(data?.paymentRequest?.beneficiaryBankName ?? data?.paymentRequest?.bank?.name) || 'N/A'}
                  />
                  <TextContent
                    label='Bank SWIFT'
                    value={(data?.paymentRequest?.beneficiaryBankSwift ?? data?.paymentRequest?.bank?.swift) || 'N/A'}
                  />
                  <TextContent
                    label='Bank Address'
                    value={(data?.paymentRequest?.beneficiaryBankAddress ?? data?.paymentRequest?.bank?.legalAddress) || 'N/A'}
                  />
                  <TextContent
                    label='Bank Account'
                    value={
                      (data?.paymentRequest?.beneficiaryBankAccountNumber ?? data?.paymentRequest?.bankAccount?.accountNumber) || 'N/A'
                    }
                  />
                </>
              ) : (
                <>
                  <TextContent
                    label='Network'
                    value={
                      (data?.paymentRequest?.beneficiaryWalletNetworkName ?? data?.paymentRequest?.beneficiaryWallet?.network?.name) ||
                      'N/A'
                    }
                  />
                  <TextContent
                    label='Wallet Address'
                    value={
                      (data?.paymentRequest?.beneficiaryWalletAddress ?? data?.paymentRequest?.beneficiaryWallet?.address) || 'N/A'
                    }
                  />
                </>
              )}
            </>
          )}
          <hr />
          <>
            <TextContent
              label='Provider'
              value={data?.paymentRequest?.serviceProvider?.name || 'N/A'}
            />
            {(data?.paymentRequest?.providerWallet || data?.paymentRequest?.providerWalletNetworkName || data?.paymentRequest?.providerWalletAddress) ? (
              <>
                <TextContent
                  label='Provider Wallet Network'
                  value={data?.paymentRequest?.providerWalletNetworkName ?? data?.paymentRequest?.providerWallet?.network?.name}
                />
                <TextContent
                  label='Provider Wallet Address'
                  value={data?.paymentRequest?.providerWalletAddress ?? data?.paymentRequest?.providerWallet?.address}
                />
              </>
            ) : (
              <>
                <TextContent
                  label='Provider Bank Name'
                  value={data?.paymentRequest?.providerBankName ?? data?.paymentRequest?.providerBank?.name}
                />
                <TextContent
                  label='Provider Bank SWIFT'
                  value={data?.paymentRequest?.providerBankSwift ?? data?.paymentRequest?.providerBank?.swift}
                />
                <TextContent
                  label='Provider Bank Address'
                  value={data?.paymentRequest?.providerBankAddress ?? data?.paymentRequest?.providerBank?.legalAddress}
                />
                <TextContent
                  label='Provider Bank Account'
                  value={
                    data?.paymentRequest?.providerBankAccountNumber ?? data?.paymentRequest?.providerBankAccount?.accountNumber
                  }
                />
              </>
            )}
          </>
          <hr />
          <TextContent
            label='Special instructions'
            value={data?.paymentRequest?.specialInstructions || 'N/A'}
          />
          {data?.paymentRequest.purposeOfRemittance && (
            <TextContent
              label='Purpose Of Remittance'
              value={
                purposeOfRemittanceItemNames[
                data?.paymentRequest.purposeOfRemittance
                ] || 'N/A'
              }
            />
          )}
          <SelectField
            name='status'
            getOptionLabel={getInvoiceStatusOptionLabel}
            items={statusSelectItems}
            disabled={isDisabledForm}
          />
          {status === PaymentStatus?.REJECTED ||
            status === PaymentStatus.POSTPONED ? (
            <Form.Item
              name='reason'
              rules={[{ required: true, message: 'This field is required', whitespace: true }]}>
              <Input.TextArea
                rows={2}
                placeholder='Reason'
                allowClear
                disabled={
                  data?.status === PaymentStatus.REJECTED ||
                  data?.status === PaymentStatus.POSTPONED ||
                  formIsLoading
                }
                value={data?.paymentRequest?.reason}
              />
            </Form.Item>
          ) : null}
          <Col>
            <Form.Item
              name='documents'
              valuePropName='fileList'
              getValueFromEvent={getFileListFromEvent}>
              <Upload
                accept={acceptUploadFileTypes}
                action={`${ROOT_URL}/api/v1/storage/upload`}
                listType='text'
                headers={{
                  Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
                }}
                multiple
                beforeUpload={beforeUpload}
                showUploadList={{
                  showRemoveIcon:
                    paymentStatus !== PaymentStatus.DRAFT &&
                      paymentStatus !== PaymentStatus.PENDING
                      ? false
                      : true,
                }}>
                <Button
                  disabled={
                    paymentStatus !== PaymentStatus.PENDING || formIsLoading
                  }
                  icon={<UploadOutlined />}>
                  &nbsp;Click to upload supporting documents
                </Button>
              </Upload>
            </Form.Item>
          </Col>
          {wiseButtonsAvailable && data?.status === PaymentStatus.APPROVED ? (
            <Col>
              <Button disabled={false} onClick={wiseConfirm}>
                Fund transfer
              </Button>
              <Button disabled={false} onClick={wiseCancel}>
                Cancel Transfer
              </Button>
            </Col>
          ) : null}
        </Form>
      </Modal>
    </>
  )
}
